import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { range } from 'utils/math';

interface UseScrollOffsetConfig {
  wait?: number;
}

interface PercentageState {
  x: number | undefined;
  y: number | undefined;
}
interface UseScrollOffsetResult<T extends HTMLElement> {
  calculateScrollPercentage: () => void;
  percentage: PercentageState;
  ref: React.RefObject<T>;
}

// TODO amend this to work for both x and y axis
export default function useScrollOffset<T extends HTMLElement>(
  opts?: UseScrollOffsetConfig,
): UseScrollOffsetResult<T> {
  const { wait = 100 } = opts || {};

  const ref = useRef<T>(null);
  const [percentage, setPercentage] = useState<PercentageState>({
    x: undefined,
    y: undefined,
  });

  const calculateScrollPercentage = useCallback(() => {
    const el = ref.current;
    if (el) {
      const maxHScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const maxVScroll = Math.max(0, el.scrollHeight - el.clientHeight);

      setPercentage({
        x: range(0, maxHScroll, 0, 1, el.scrollLeft),
        y: range(0, maxVScroll, 0, 1, el.scrollTop),
      });
    }
  }, []);

  useEffect(() => {
    const el = ref.current;

    const handleScroll = throttle(calculateScrollPercentage, wait);

    if (el) {
      el.addEventListener('scroll', handleScroll);

      return () => {
        el.removeEventListener('scroll', handleScroll);
      };
    }
  }, [calculateScrollPercentage, wait]);

  return {
    calculateScrollPercentage,
    percentage,
    ref,
  };
}
